import { DateRange } from 'bold-ui'
import { AgeRangeValue } from 'components/date/AgeRange'
import { EquipeSelectModel, RadioOption, SimNaoEnum } from 'components/form'
import { CiapCidSelectModel } from 'components/form/field/select/CiapCidSelectField/CiapCidSelectField'
import { GrupoCondicaoSelectModel } from 'components/form/field/select/GrupoCondicaoSelectField/GrupoCondicaoSelectField'
import { RacaCorSelectModel } from 'components/form/field/select/RacaCorSelectField'
import { startOfDay, subMonths } from 'date-fns'
import {
  AcompanhamentoCondicaoSaudeTematicaEnum,
  AgeRangeType,
  CondicaoMaternalFiltroEnum,
  FaixaEtariaEnum,
  FaixaEtariaInput,
  GrupoCondicaoEnum,
  IvcfSubgrupoPerguntaEnum,
  PageParams,
  PeriodoCadastroIndividualEnum,
  RiscoVulnerabilidadeIvcfEnum,
  SexoEnum,
} from 'graphql/types.generated'
import { idGrupoCondicao } from 'types/enums'
import { AcompanhamentoFilterModel } from 'view/acompanhamento/model/model-acompanhamentoFilter'

export const ACOMPANHAMENTO_CONDICAO_SAUDE_ATMOSPHERE_PATH = 'acompanhamento-condicao-saude'

export enum AgeRangeFilter {
  TODAS_FAIXAS = 'TODAS_FAIXAS',
  CRIANCA = 'CRIANCA',
  ADOLESCENTE = 'ADOLESCENTE',
  ADULTO = 'ADULTO',
  IDOSO = 'IDOSO',
  OUTRA = 'OUTRA',
}

export interface AcompanhamentoCondicaoSaudeFormModel {
  unidadeResponsavelId?: ID
  equipeResponsavel?: EquipeSelectModel
  isCoordenador?: boolean
  microareas?: string[]
  tria?: TriaFilterModel
  tipoVulnerabilidadeSelected?: TipoVulnerabilidadeEnum
  pageParams?: PageParams
  tematica?: AcompanhamentoCondicaoSaudeTematicaEnum
  filtrosAvancados?: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel
}

export interface AcompanhamentoCondicaoSaudeFiltrosAvancadosModel extends AcompanhamentoFilterModel {
  grupoCondicao?: GrupoCondicaoSelectModel[]
  problemasCondicoes?: CiapCidSelectModel[]
  ativoListaProblema?: boolean
  naoIncluidoListaProblema?: boolean
  periodoUltimoAtendimento?: DateRange
  ivcf?: IvcfFilterModel
  racaCor?: RacaCorSelectModel
  condicaoMaternal?: CondicaoMaternalFiltroEnum
  beneficiarioBolsaFamilia?: SimNaoEnum
}

export const faixaEtariaRecord: Partial<Record<FaixaEtariaEnum, FaixaEtariaInput>> = {
  [FaixaEtariaEnum.CRIANCA]: { idadeMaxima: 10, tipoFaixaEtaria: AgeRangeType.ANOS },
  [FaixaEtariaEnum.ADOLESCENTE]: { idadeMinima: 11, idadeMaxima: 19, tipoFaixaEtaria: AgeRangeType.ANOS },
  [FaixaEtariaEnum.ADULTO]: { idadeMinima: 20, idadeMaxima: 59, tipoFaixaEtaria: AgeRangeType.ANOS },
  [FaixaEtariaEnum.IDOSO]: { idadeMinima: 60, tipoFaixaEtaria: AgeRangeType.ANOS },
}

export interface IvcfFilterModel {
  riscoVulnerabilidade?: RiscoVulnerabilidadeIvcfEnum[]
  subgrupo?: IvcfSubgrupoPerguntaEnum[]
  pontuacao?: AgeRangeValue
  buscaPorPontuacao?: boolean
  exibirApenasCidadaosIdososSemIvcf?: boolean
}

export interface TriaFilterModel {
  periodoRegistro?: PeriodoCadastroIndividualEnum
  resultadosTria?: ResultadosTriaEnum
}

export enum TipoVulnerabilidadeEnum {
  TRIA = 'TRIA',
  IVCF = 'IVCF',
}

export enum ResultadosTriaEnum {
  RISCO_DE_INSEGURANCA_ALIMENTAR = 'RISCO_DE_INSEGURANCA_ALIMENTAR',
  SEM_REGISTRO_TRIA_NO_PERIODO = 'SEM_REGISTRO_TRIA_NO_PERIODO',
}

export const resultadosTriaOptions: RadioOption<ResultadosTriaEnum>[] = [
  {
    value: ResultadosTriaEnum.RISCO_DE_INSEGURANCA_ALIMENTAR,
    label: 'Exibir apenas pessoas que vivem em domicílios em risco de insegurança alimentar',
  },
  {
    value: ResultadosTriaEnum.SEM_REGISTRO_TRIA_NO_PERIODO,
    label: 'Exibir apenas pessoas que vivem em domicílios sem registro da TRIA no período',
  },
]

export const periodoRegistroTriaRecord: Record<PeriodoCadastroIndividualEnum, string> = {
  [PeriodoCadastroIndividualEnum.SEIS_MESES]: 'Últimos 6 meses',
  [PeriodoCadastroIndividualEnum.DOZE_MESES]: 'Últimos 12 meses',
}

export const condicaoMaternalFiltroRecord: Record<CondicaoMaternalFiltroEnum, string> = {
  [CondicaoMaternalFiltroEnum.TODAS]: 'Todas',
  [CondicaoMaternalFiltroEnum.GESTANTE]: 'Gestante',
  [CondicaoMaternalFiltroEnum.PUERPERA]: 'Puérpera',
}

const commonDefaultFilterAcompanhamentoCondicaoSaude: AcompanhamentoCondicaoSaudeFormModel = {
  tria: null,
  tipoVulnerabilidadeSelected: null,
  filtrosAvancados: {
    periodoUltimoAtendimento: {
      startDate: subMonths(startOfDay(new Date()), 36),
      endDate: startOfDay(new Date()),
    },
    grupoCondicao: [],
    problemasCondicoes: [],
    ativoListaProblema: false,
    naoIncluidoListaProblema: false,
    identidadeGenero: null,
    sexo: null,
    tipoFaixaEtaria: FaixaEtariaEnum.TODAS_FAIXAS,
    faixaEtariaRange: null,
    racaCor: null,
    condicaoMaternal: null,
    beneficiarioBolsaFamilia: null,
  },
}

export const defaultFilterAcompanhamentoCondicaoSaudeByTematica: Partial<Record<
  AcompanhamentoCondicaoSaudeTematicaEnum,
  AcompanhamentoCondicaoSaudeFormModel
>> = {
  GERAL: {
    ...commonDefaultFilterAcompanhamentoCondicaoSaude,
  },
  GESTANTE_PUERPERA: {
    ...commonDefaultFilterAcompanhamentoCondicaoSaude,
    filtrosAvancados: {
      ...commonDefaultFilterAcompanhamentoCondicaoSaude.filtrosAvancados,
      condicaoMaternal: CondicaoMaternalFiltroEnum.TODAS,
    },
  },
  PESSOA_IDOSA: {
    ...commonDefaultFilterAcompanhamentoCondicaoSaude,
    filtrosAvancados: {
      ...commonDefaultFilterAcompanhamentoCondicaoSaude.filtrosAvancados,
      tipoFaixaEtaria: FaixaEtariaEnum.IDOSO,
    },
  },
  DIABETES: {
    ...commonDefaultFilterAcompanhamentoCondicaoSaude,
    filtrosAvancados: {
      ...commonDefaultFilterAcompanhamentoCondicaoSaude.filtrosAvancados,
      ativoListaProblema: true,
      grupoCondicao: [
        { id: idGrupoCondicao[GrupoCondicaoEnum.DIABETES], grupo: GrupoCondicaoEnum.DIABETES },
      ] as GrupoCondicaoSelectModel[],
    },
  },
  HIPERTENSAO_ARTERIAL: {
    ...commonDefaultFilterAcompanhamentoCondicaoSaude,
    filtrosAvancados: {
      ...commonDefaultFilterAcompanhamentoCondicaoSaude.filtrosAvancados,
      ativoListaProblema: true,
      grupoCondicao: [
        { id: idGrupoCondicao[GrupoCondicaoEnum.HIPERTENSAO_ARTERIAL], grupo: GrupoCondicaoEnum.HIPERTENSAO_ARTERIAL },
      ] as GrupoCondicaoSelectModel[],
    },
  },
  SAUDE_MULHER: {
    ...commonDefaultFilterAcompanhamentoCondicaoSaude,
    filtrosAvancados: {
      ...commonDefaultFilterAcompanhamentoCondicaoSaude.filtrosAvancados,
      sexo: [SexoEnum.FEMININO, SexoEnum.INDETERMINADO],
      tipoFaixaEtaria: FaixaEtariaEnum.OUTRA,
      faixaEtariaRange: {
        tipoFaixaEtaria: AgeRangeType.ANOS,
        firstValue: 14,
        secondValue: 69,
      },
    },
  },
  DESENVOLVIMENTO_INFANTIL: {
    ...commonDefaultFilterAcompanhamentoCondicaoSaude,
    filtrosAvancados: {
      ...commonDefaultFilterAcompanhamentoCondicaoSaude.filtrosAvancados,
      tipoFaixaEtaria: FaixaEtariaEnum.OUTRA,
      faixaEtariaRange: {
        tipoFaixaEtaria: AgeRangeType.ANOS,
        firstValue: 0,
        secondValue: 4,
      },
    },
  },
  SAUDE_BUCAL: { ...commonDefaultFilterAcompanhamentoCondicaoSaude },
}
